import type { BaseQueryFn, FetchArgs, FetchBaseQueryArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { fetchBaseQuery, retry } from "@reduxjs/toolkit/query/react";

import { handleApiError } from "./error.http";
import { handleConfirmModal, handleToast } from "./extra.http";
import { isPromise } from "./guards.http";
import { generateRequestMetadata, logRequest, logResponse } from "./interceptors.http";
import { requestMonitor } from "./monitor.http";
import type { ExtraOptions } from "./types.http";

export type BaseQueryWithMeta = BaseQueryFn<FetchArgs | string, unknown, FetchBaseQueryError, ExtraOptions, ReturnType<typeof generateRequestMetadata>>;

export type BaseQuery = BaseQueryFn<FetchArgs | string, unknown, FetchBaseQueryError, ExtraOptions>;

// const baseUrl = import.meta.env.VITE_APP_API_URL

/**
 * Base query with retry logic
 * Retries failed requests once by default
 */
const baseQueryWithRetry = ({ baseUrl, ...fetchArgs }: FetchBaseQueryArgs) =>
  retry<BaseQuery>(
    async (args, api, extraOptions) => {
      const version = extraOptions?.version;
      const dynamicBaseUrl = version ? `${baseUrl}/${version}` : baseUrl;

      const dynamicBaseQuery = fetchBaseQuery({
        ...fetchArgs,
        baseUrl: dynamicBaseUrl
      });

      return dynamicBaseQuery(args, api, extraOptions);
    },
    { maxRetries: 0 }
  );

/**
 * Main base query with authentication, logging, and role-based URL modification
 */
export const baseQuery: (fetchArgs: FetchBaseQueryArgs) => BaseQueryWithMeta = (fetchArgs) => async (args, api, extraOptions) => {
  // Generate request metadata for logging
  const metadata = generateRequestMetadata();

  const modalResult = await handleConfirmModal(extraOptions);
  if (modalResult) {
    return {
      ...modalResult,
      meta: metadata
    };
  }

  // Get auth state
  /* const { auth } = api.getState() as RootState */

  // Apply role-based URL modifications
  const modifiedArgs: FetchArgs | string | null = args;

  // ======== Log request ========
  logRequest(modifiedArgs, metadata, false);
  // ======== Monitor request ========
  requestMonitor.logRequest(modifiedArgs, metadata.requestId, metadata.timestamp);

  // ======== Execute request with retry logic ========
  const promise = baseQueryWithRetry(fetchArgs)(modifiedArgs, api, extraOptions);

  if (isPromise(promise)) {
    handleToast(extraOptions, promise);
  }

  const result = await promise;

  // ======== Log response ========
  logResponse(result, metadata, false);
  // ======== Monitor response ========
  requestMonitor.logResponse(result, metadata.requestId, metadata.timestamp);
  // ======== Handle errors (401, 403, etc.) ========
  const handledResult = handleApiError(result.error, api, result);

  // ======== Attach metadata to result ========
  return {
    ...handledResult,
    meta: { ...handledResult.meta, ...metadata }
  };
};
